import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import bugReportService, {
  BugReport,
  BugReportPriority,
  BugReportStatus,
} from '../services/api/bugReportService';

const STATUS_OPTIONS: Array<{ value: 'all' | BugReportStatus; label: string }> = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'wontfix', label: "Won't Fix" },
];

const PRIORITY_OPTIONS: Array<{ value: 'all' | BugReportPriority; label: string }> = [
  { value: 'all', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const statusColor = (status: BugReportStatus): 'default' | 'primary' | 'success' | 'warning' => {
  if (status === 'new') return 'primary';
  if (status === 'in_progress') return 'warning';
  if (status === 'resolved' || status === 'closed') return 'success';
  return 'default';
};

const priorityColor = (priority: BugReportPriority): 'default' | 'info' | 'warning' | 'error' => {
  if (priority === 'critical') return 'error';
  if (priority === 'high') return 'warning';
  if (priority === 'medium') return 'info';
  return 'default';
};

const readable = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export default function BugReportsPage() {
  const [reports, setReports] = useState<BugReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [status, setStatus] = useState<'all' | BugReportStatus>('all');
  const [priority, setPriority] = useState<'all' | BugReportPriority>('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState({ new: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatingReportId, setUpdatingReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  const totalPages = useMemo(() => Math.max(Math.ceil(count / pageSize), 1), [count]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bugReportService.getBugReports({
        status,
        priority,
        search: appliedSearch,
        page,
        page_size: pageSize,
      });
      setReports(data.results);
      setCount(data.count);
      setSummary(data.summary);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load bug reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority, appliedSearch, page]);

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const handleUpdate = async (updates: Partial<Pick<BugReport, 'status' | 'priority'>>) => {
    if (!selectedReport) return;
    try {
      setUpdating(true);
      const updated = await bugReportService.updateBugReport(selectedReport.id, updates);
      setSelectedReport(updated);
      setReports((prev) => prev.map((report) => (report.id === updated.id ? updated : report)));
      await loadReports();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update bug report.');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusUpdate = async (report: BugReport, nextStatus: BugReportStatus) => {
    if (report.status === nextStatus) return;

    try {
      setUpdatingReportId(report.id);
      setError(null);
      const updated = await bugReportService.updateBugReport(report.id, { status: nextStatus });
      setReports((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setSelectedReport((current) => (current?.id === updated.id ? updated : current));
      await loadReports();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update bug report status.');
    } finally {
      setUpdatingReportId(null);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Bug Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reports submitted from client and artisan settings are saved here.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadReports} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="caption" color="text.secondary">New</Typography><Typography variant="h5" fontWeight={700}>{summary.new}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="caption" color="text.secondary">In Progress</Typography><Typography variant="h5" fontWeight={700}>{summary.in_progress}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="caption" color="text.secondary">Resolved</Typography><Typography variant="h5" fontWeight={700}>{summary.resolved}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="caption" color="text.secondary">Closed</Typography><Typography variant="h5" fontWeight={700}>{summary.closed}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Search reports"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField select fullWidth size="small" label="Status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value as any); }}>
                {STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField select fullWidth size="small" label="Priority" value={priority} onChange={(e) => { setPage(1); setPriority(e.target.value as any); }}>
                {PRIORITY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={applySearch}>
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Report</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <BugReportIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No bug reports found.</Typography>
                  </TableCell>
                </TableRow>
              ) : reports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>{report.title}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{report.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{report.user?.name || report.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{report.user?.user_type || 'guest'}</Typography>
                  </TableCell>
                  <TableCell><Chip size="small" color={statusColor(report.status)} label={readable(report.status)} /></TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      select
                      size="small"
                      label="Set status"
                      value={report.status}
                      onChange={(e) => handleStatusUpdate(report, e.target.value as BugReportStatus)}
                      disabled={updatingReportId === report.id}
                      fullWidth
                    >
                      {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell><Chip size="small" color={priorityColor(report.priority)} label={readable(report.priority)} /></TableCell>
                  <TableCell>{new Date(report.created_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" endIcon={<OpenIcon />} onClick={() => setSelectedReport(report)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">{count} report{count === 1 ? '' : 's'}</Typography>
          <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} />
        </Box>
      </Card>

      <Dialog open={Boolean(selectedReport)} onClose={() => setSelectedReport(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{selectedReport?.title}</DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Stack spacing={2}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip color={statusColor(selectedReport.status)} label={readable(selectedReport.status)} />
                <Chip color={priorityColor(selectedReport.priority)} label={readable(selectedReport.priority)} />
                <Chip label={selectedReport.user?.user_type || 'guest'} />
              </Stack>
              <Typography whiteSpace="pre-wrap">{selectedReport.description}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Contact Email</Typography>
                  <Typography>{selectedReport.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Submitted</Typography>
                  <Typography>{new Date(selectedReport.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Status"
                    value={selectedReport.status}
                    onChange={(e) => handleUpdate({ status: e.target.value as BugReportStatus })}
                    disabled={updating}
                  >
                    {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Priority"
                    value={selectedReport.priority}
                    onChange={(e) => handleUpdate({ priority: e.target.value as BugReportPriority })}
                    disabled={updating}
                  >
                    {PRIORITY_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Device Info</Typography>
                <Box component="pre" sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
                  {selectedReport.device_info || 'No device info provided.'}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedReport(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
